import { useEffect, useMemo, useRef, useState } from 'react';
import Input from './Input';
import Button from './Button';

const toRows = (dict) => {
    if (!dict || typeof dict !== 'object' || Array.isArray(dict)) return [];
    return Object.entries(dict).map(([key, value]) => ({ key, value }));
};

const fromRows = (rows, valueMode) => {
    const out = {};
    for (const row of rows) {
        const key = (row.key || '').trim();
        if (!key) continue;
        if (valueMode === 'tags') {
            const list = Array.isArray(row.value)
                ? row.value
                : String(row.value || '')
                      .split(',')
                      .map((v) => v.trim())
                      .filter(Boolean);
            if (list.length > 0) out[key] = list;
        } else {
            const raw = String(row.value ?? '').trim();
            if (raw) out[key] = raw;
        }
    }
    return out;
};

const rowsFromValue = (value, valueMode) =>
    toRows(value).map((row) => ({
        key: row.key,
        value:
            valueMode === 'tags'
                ? (Array.isArray(row.value) ? row.value.join(', ') : String(row.value || ''))
                : String(row.value ?? ''),
    }));

const formatDefaultPreview = (dict, valueMode) => {
    if (!dict || typeof dict !== 'object' || Array.isArray(dict)) return '';
    const entries = Object.entries(dict);
    if (entries.length === 0) return '';
    return entries
        .map(([key, value]) => {
            if (valueMode === 'tags') {
                const list = Array.isArray(value) ? value : [value];
                return `${key} → ${list.join(', ')}`;
            }
            return `${key} → ${value}`;
        })
        .join(' · ');
};

export default function KeyValueListEditor({
    label,
    hint,
    value,
    onChange,
    keyOptions = null,
    keyPlaceholder = 'key',
    valueMode = 'text',
    valuePlaceholder = 'value',
    defaultValue = null,
    addLabel = 'Add entry',
}) {
    const [rows, setRows] = useState(() => rowsFromValue(value, valueMode));
    const lastEmittedRef = useRef(JSON.stringify(value || {}));

    useEffect(() => {
        const incoming = JSON.stringify(value || {});
        if (incoming === lastEmittedRef.current) return;
        setRows(rowsFromValue(value, valueMode));
        lastEmittedRef.current = incoming;
    }, [value, valueMode]);

    const defaultPreview = useMemo(
        () => formatDefaultPreview(defaultValue, valueMode),
        [defaultValue, valueMode],
    );

    const emit = (nextRows) => {
        setRows(nextRows);
        const dict = fromRows(nextRows, valueMode);
        lastEmittedRef.current = JSON.stringify(dict);
        onChange(dict);
    };

    const updateRow = (index, patch) => {
        const next = rows.map((row, i) => (i === index ? { ...row, ...patch } : row));
        emit(next);
    };

    const removeRow = (index) => {
        emit(rows.filter((_, i) => i !== index));
    };

    const addRow = () => {
        emit([...rows, { key: '', value: '' }]);
    };

    const keyOptionList = Array.isArray(keyOptions) ? keyOptions : null;
    const datalistId = keyOptionList ? `kvkeys-${label?.replace(/\s+/g, '-') || 'list'}` : null;

    return (
        <div>
            {label && (
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    {label}
                    {hint && <span className="text-xs text-gray-400 font-normal ml-1.5">{hint}</span>}
                </label>
            )}

            {keyOptionList && (
                <datalist id={datalistId}>
                    {keyOptionList.map((opt) => (
                        <option key={typeof opt === 'string' ? opt : opt.value} value={typeof opt === 'string' ? opt : opt.value} />
                    ))}
                </datalist>
            )}

            <div className="space-y-2">
                {rows.length === 0 ? (
                    <p className="text-xs text-gray-400 italic">
                        {defaultPreview ? `Leave empty to inherit defaults: ${defaultPreview}` : 'No entries — inherit defaults.'}
                    </p>
                ) : (
                    rows.map((row, index) => (
                        <div key={index} className="flex items-start gap-2">
                            <Input
                                className="flex-1"
                                list={datalistId || undefined}
                                value={row.key}
                                placeholder={keyPlaceholder}
                                onChange={(e) => updateRow(index, { key: e.target.value })}
                            />
                            <span className="text-gray-400 pt-1.5 select-none">→</span>
                            <Input
                                className="flex-1"
                                value={row.value}
                                placeholder={valuePlaceholder}
                                onChange={(e) => updateRow(index, { value: e.target.value })}
                            />
                            <button
                                type="button"
                                onClick={() => removeRow(index)}
                                className="h-9 w-9 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                aria-label="Remove entry"
                            >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    ))
                )}
            </div>

            <div className="mt-2 flex items-center justify-between">
                <Button type="button" variant="secondary" size="sm" onClick={addRow}>
                    + {addLabel}
                </Button>
                {rows.length > 0 && defaultPreview && (
                    <span className="text-xs text-gray-400 italic truncate ml-2" title={defaultPreview}>
                        default: {defaultPreview}
                    </span>
                )}
            </div>
        </div>
    );
}
